import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { SearchWidget } from "@/features/portal/SearchWidget";
import { useAuthStore } from "@/lib/auth";
import { useWorkspaceStore } from "@/lib/workspace";
import { usePortalDetail } from "@/routes/portals.$id";
import { createFileRoute } from "@tanstack/react-router";
import { Check, Copy, ExternalLink } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const Route = createFileRoute("/portals/$id/search")({
	component: SearchTab,
});

function SearchTab() {
	const { t } = useTranslation("portals");
	const ctx = usePortalDetail();
	const token = useAuthStore((s) => s.token);
	const workspaceId = useWorkspaceStore((s) => s.selectedId);

	const [theme, setTheme] = useState<"light" | "dark" | "auto">("auto");
	const [accent, setAccent] = useState("#4f46e5");
	const [locale, setLocale] = useState<"ru" | "en">("ru");
	const [placeholder, setPlaceholder] = useState("");
	const [showBrand, setShowBrand] = useState(true);
	const [showPrice, setShowPrice] = useState(true);
	const [showDesc, setShowDesc] = useState(false);
	const [maxResults, setMaxResults] = useState("20");
	const [borderRadius, setBorderRadius] = useState("12");

	const embedParams = useMemo(() => {
		const p = new URLSearchParams();
		p.set("key", "YOUR_API_KEY");
		p.set("api", ctx.portalApiUrl);
		p.set("theme", theme);
		if (accent !== "#4f46e5") p.set("accent", accent.replace("#", ""));
		p.set("locale", locale);
		if (placeholder) p.set("placeholder", placeholder);
		if (!showBrand) p.set("brand", "0");
		if (!showPrice) p.set("price", "0");
		if (showDesc) p.set("desc", "1");
		if (maxResults !== "20") p.set("max", maxResults);
		if (borderRadius !== "12") p.set("radius", borderRadius);
		return p.toString();
	}, [
		theme,
		accent,
		locale,
		placeholder,
		showBrand,
		showPrice,
		showDesc,
		maxResults,
		borderRadius,
		ctx.portalApiUrl,
	])

	const embedCode = `<iframe src="${window.location.origin}/widget?${embedParams}" style="width:100%;border:none;min-height:480px" loading="lazy"></iframe>`;

	const [copied, setCopied] = useState(false);
	const copyEmbed = () => {
		navigator.clipboard.writeText(embedCode);
		setCopied(true);
		toast.success(t("code_copied"));
		setTimeout(() => setCopied(false), 2000);
	}

	const previewBg =
		theme === "dark" ? "#0f172a" : theme === "light" ? "#ffffff" : undefined;

	if (!token) {
		return (
			<Card className="px-4 py-8 text-center">
				<p className="text-sm text-muted-foreground">{t("auth_required")}</p>
			</Card>
		)
	}

	return (
		<div className="space-y-5 min-w-0">
			<h2 className="text-lg font-semibold">{t("search_widget")}</h2>

			<div className="flex gap-5 min-w-0">
				{/* ── Preview ─────────────────────────── */}
				<div className="flex-1 min-w-0 space-y-3">
					<div
						className="rounded-lg border p-4"
						style={{ background: previewBg, minHeight: 500 }}
					>
						<SearchWidget
							config={{
								apiKey: token,
								apiUrl: ctx.portalApiUrl,
								theme,
								accentColor: accent,
								borderRadius: `${borderRadius}px`,
								locale,
								placeholder: placeholder || undefined,
								showBrand,
								showPrice,
								showDescription: showDesc,
								maxResults: Number.parseInt(maxResults, 10),
								workspaceId: workspaceId ?? undefined,
							}}
						/>
					</div>

					{/* Embed code */}
					<div className="relative">
						<pre className="bg-muted rounded-md p-3 text-xs text-muted-foreground leading-relaxed whitespace-pre-wrap break-all">
							{embedCode}
						</pre>
						<Button
							variant="ghost"
							size="sm"
							className="absolute top-1.5 right-1.5 h-7 px-2"
							onClick={copyEmbed}
						>
							{copied ? (
								<Check className="h-3.5 w-3.5" />
							) : (
								<Copy className="h-3.5 w-3.5" />
							)}
						</Button>
					</div>
				</div>

				{/* ── Controls ────────────────────────── */}
				<div className="w-[260px] shrink-0 space-y-5">
					<Section title={t("theme")}>
						<Select
							value={theme}
							onValueChange={(v) => setTheme(v as typeof theme)}
						>
							<SelectTrigger className="h-8 text-sm">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="auto">{t("theme_auto")}</SelectItem>
								<SelectItem value="light">{t("theme_light")}</SelectItem>
								<SelectItem value="dark">{t("theme_dark")}</SelectItem>
							</SelectContent>
						</Select>
					</Section>

					<Section title={t("accent")}>
						<div className="flex items-center gap-2">
							<Input
								type="color"
								value={accent}
								onChange={(e) => setAccent(e.target.value)}
								className="h-8 w-8 rounded border cursor-pointer p-0.5"
							/>
							<Input
								value={accent}
								onChange={(e) => setAccent(e.target.value)}
								className="h-8 text-sm font-mono flex-1"
							/>
						</div>
					</Section>

					<Section title={t("language")}>
						<Select
							value={locale}
							onValueChange={(v) => setLocale(v as typeof locale)}
						>
							<SelectTrigger className="h-8 text-sm">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="ru">{t("lang_russian")}</SelectItem>
								<SelectItem value="en">{t("lang_english")}</SelectItem>
							</SelectContent>
						</Select>
					</Section>

					<Section title={t("hint_placeholder")}>
						<Input
							value={placeholder}
							onChange={(e) => setPlaceholder(e.target.value)}
							placeholder={t("search_placeholder")}
							className="h-8 text-sm"
						/>
					</Section>

					<Section title={t("border_radius")}>
						<Input
							type="number"
							value={borderRadius}
							onChange={(e) => setBorderRadius(e.target.value)}
							min={0}
							max={24}
							className="h-8 text-sm w-20"
						/>
					</Section>

					<Section title={t("results_count")}>
						<Select value={maxResults} onValueChange={setMaxResults}>
							<SelectTrigger className="h-8 text-sm w-20">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="6">6</SelectItem>
								<SelectItem value="12">12</SelectItem>
								<SelectItem value="20">20</SelectItem>
								<SelectItem value="40">40</SelectItem>
							</SelectContent>
						</Select>
					</Section>

					<div className="border-t pt-4 space-y-3">
						<Row
							label={t("brand")}
							checked={showBrand}
							onChange={setShowBrand}
						/>
						<Row
							label={t("price")}
							checked={showPrice}
							onChange={setShowPrice}
						/>
						<Row
							label={t("description")}
							checked={showDesc}
							onChange={setShowDesc}
						/>
					</div>

					<div className="border-t pt-4">
						<Button
							variant="outline"
							size="sm"
							className="w-full"
							onClick={() => window.open(`/widget?${embedParams}`, "_blank")}
						>
							<ExternalLink className="h-3.5 w-3.5 mr-1.5" />
							{t("open_separately")}
						</Button>
					</div>
				</div>
			</div>
		</div>
	)
}

function Section({
	title,
	children,
}: { title: string; children: React.ReactNode }) {
	return (
		<div className="space-y-1.5">
			<Label className="text-xs text-muted-foreground">{title}</Label>
			{children}
		</div>
	)
}

function Row({
	label,
	checked,
	onChange,
}: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
	return (
		<Label className="flex items-center justify-between cursor-pointer">
			<span className="text-sm">{label}</span>
			<Switch checked={checked} onCheckedChange={onChange} />
		</Label>
	)
}
